import { ArrowDownIcon, Card, Dropdown, DropdownMenu, DropdownMenuItem } from '@bp/ui-components';
import classNames from 'classnames';
import { FC } from 'react';
import styles from './MenuBar.module.scss';

export type MenuBarEntry = {
  actions: {
    icon: React.ReactNode;
    title: string;
    /** Optional marker (e.g. a Beta chip) floated into the entry's top-right corner. */
    badge?: React.ReactNode;
    action: () => Promise<void> | void;
    disabled?: boolean;
    /** Hebt einen Top-Level-Eintrag als aktiv/ausgewählt hervor (flache Toggle-/Ansichts-Buttons). */
    active?: boolean;
    subactions?: Subaction[];
  }[];
};

export type Subaction = {
  title?: string | null;
  disabled?: boolean;
  action?: (() => void) | null;
  /**
   * 'heading' beschriftet einen Abschnitt innerhalb eines Dropdowns. Das UI-Kit kennt dafür
   * keinen eigenen Typ, deshalb wird die Überschrift hier einmalig als (nicht klickbarer)
   * 'component'-Eintrag gebaut — Aufrufer schreiben nur `{ type: 'heading', title }`.
   */
  type?: 'ruler' | 'switch' | 'component' | 'default' | 'heading';
  value?: boolean;
  node?: React.ReactNode;
};

export type MenuBarProps = {
  entries: MenuBarEntry[];
  usePortal?: boolean;
  passive?: boolean;
  className?: string | undefined;
};

export const MenuBar: FC<MenuBarProps> = ({ entries, usePortal, passive, className }) => {
  function subactionsToMenuEntry(subactions: Subaction[]): DropdownMenuItem[] {
    return subactions.map((subaction) => {
      if (subaction.type === 'heading') {
        return {
          type: 'component',
          // `pointer-events: none` am Eintrag: eine Überschrift ist kein Klickziel und darf das
          // Menü nicht schließen.
          className: styles['menu-heading'],
          node: <span className={styles['menu-heading-label']}>{subaction.title}</span>,
        };
      }
      return {
        label: subaction.title ?? undefined,
        disabled: subaction.disabled ?? false,
        onClick: subaction.action ?? undefined,
        type: subaction.type ?? 'default',
        value: subaction.value ?? undefined,
        onValueChange: subaction.action ?? undefined,
        node: subaction.node ?? undefined,
      };
    });
  }

  const menuBarClasses = classNames(styles['menu-bar'], className);

  return (
    <div className={menuBarClasses}>
      {entries.length === 0 ? (
        <></>
      ) : (
        entries.map((entry, index) => {
          return entry.actions.length === 0 ? (
            <></>
          ) : (
            <Card
              hideBackground={true}
              key={index}
              className={classNames(styles['menu-block'], {
                [styles.single]: entry.actions.length <= 1,
              })}
              wrapperClass={classNames(styles.wrapper, {
                [styles['is-passive']]: passive,
              })}
              wrapperStyle={{
                width: 'fit-content',
                padding: 'var(--spacing-3)',
              }}
              contentPadding='none'
              fixedHeight='var(--card-menu-height)'
            >
              {entry.actions.map((action, index) => {
                return action.subactions && action.subactions.length > 1 ? (
                  // multiaction entry with drop-down
                  <div key={index} className={classNames(styles.entry, { [styles.disabled]: action.disabled })}>
                    {action.badge && <div className={styles.badge}>{action.badge}</div>}
                    <Dropdown
                      usePortal={usePortal}
                      trigger={
                        <div className={styles.title}>
                          {action.icon}
                          {/* Text bleibt mittig unter dem Icon; der Pfeil sitzt absolut daneben
                              und beeinflusst die Zentrierung nicht. */}
                          <span className={styles.label}>
                            {action.title}
                            <span className={styles.indicator}>
                              <ArrowDownIcon />
                            </span>
                          </span>
                        </div>
                      }
                      disabled={action.disabled}
                    >
                      <DropdownMenu usePortal={usePortal} data={subactionsToMenuEntry(action.subactions)} />
                    </Dropdown>
                  </div>
                ) : (
                  // single action entry
                  <div
                    key={index}
                    className={classNames(styles.entry, {
                      [styles.disabled]: action.disabled,
                      [styles.active]: action.active,
                    })}
                    onClick={action.disabled ? undefined : action.action}
                  >
                    {action.badge && <div className={styles.badge}>{action.badge}</div>}
                    <div className={styles.title}>
                      {action.icon}
                      {action.title}
                    </div>
                  </div>
                );
              })}
            </Card>
          );
        })
      )}
    </div>
  );
};
